// Sprint 12 MPT.B2 — Meetings tab on the project dashboard.
//
// Two views in one component, switched by `selectedMeetingId`:
//   1. List   — every meeting in the project with extraction status
//               and pending-review counts.
//   2. Detail — Decisions / Actions / Risks of a chosen meeting,
//               each item rendered as a card with Approve / Edit /
//               Dismiss inline actions.
//
// All state mutations are optimistic: we update local state on click,
// fire the POST, and roll back on error. Approval promotes the JSONB
// item to its canonical table (decisions / risks / action_items) on
// the server; the response carries the new canonicalId which we
// stash in the local item so the badge can switch from "pending" to
// "approved".

import { useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api';
import MeetingUploadDialog from './MeetingUploadDialog';

// ─── Types ────────────────────────────────────────────────────────────

type ExtractionStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
type ReviewStatus = 'PENDING_REVIEW' | 'REVIEWED';
type ItemReviewStatus = 'pending' | 'approved' | 'dismissed';
type ItemCategory = 'decisions' | 'actions' | 'risks';

interface CountSlot { total: number; pending: number }

export interface MeetingListItem {
  id: string;
  title: string;
  meetingDate: string | null;
  attendees: string[];
  createdAt: string;
  updatedAt: string;
  transcript: {
    id: string;
    extractionStatus: ExtractionStatus;
    extractionAttempts: number;
    reviewStatus: ReviewStatus;
    counts: { decisions: CountSlot; actions: CountSlot; risks: CountSlot };
  } | null;
}

export interface MeetingItemBase {
  reviewStatus?: ItemReviewStatus;
  reviewedAt?:   string;
  reviewedBy?:   string;
  canonicalId?:  string;
}
export interface DecisionItem extends MeetingItemBase {
  title: string;
  rationale?: string;
  decidedBy?: string;
}
export interface ActionItem extends MeetingItemBase {
  description: string;
  assignee?:   string;
  dueDate?:    string;
}
export interface RiskItem extends MeetingItemBase {
  description: string;
  impact?:     string;
  likelihood?: string;
}

interface MeetingDetail {
  meeting: {
    id:           string;
    title:        string;
    meetingDate:  string | null;
    attendees:    string[];
    createdAt:    string;
    updatedAt:    string;
  };
  transcript: {
    id:                 string;
    transcriptFormat:   'vtt' | 'text';
    extractionStatus:   ExtractionStatus;
    extractionAttempts: number;
    extractionError:    string | null;
    extractedDecisions: DecisionItem[];
    extractedActions:   ActionItem[];
    extractedRisks:     RiskItem[];
    reviewStatus:       ReviewStatus;
    createdAt:          string;
    updatedAt:          string;
  } | null;
}

// ─── List view ────────────────────────────────────────────────────────

interface MeetingsTabProps {
  projectId: string;
}

export default function MeetingsTab({ projectId }: MeetingsTabProps) {
  const [list, setList] = useState<MeetingListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  // Bumped after a successful upload so the list useEffect re-fires
  // and the new meeting appears without a full remount.
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ meetings: MeetingListItem[] }>(`/projects/${projectId}/meetings`)
      .then(r => {
        if (cancelled) return;
        // Reset prior error inside the resolution callback rather than
        // synchronously at effect start — avoids a cascading-render
        // warning from react-hooks/set-state-in-effect.
        setError(null);
        setList(r.meetings);
      })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load meetings'); });
    return () => { cancelled = true; };
  }, [projectId, refreshNonce]);

  const onUploadSuccess = () => {
    setShowUpload(false);
    setRefreshNonce(n => n + 1);
  };

  const uploadDialog = showUpload ? (
    <MeetingUploadDialog
      projectId={projectId}
      onClose={() => setShowUpload(false)}
      onSuccess={onUploadSuccess}
    />
  ) : null;

  if (error) {
    return (
      <>
        <div data-testid="meetings-error" style={{ padding: 24, color: 'var(--error)', fontFamily: 'var(--font-body)' }}>
          Failed to load meetings: {error}
        </div>
        {uploadDialog}
      </>
    );
  }

  if (selectedMeetingId) {
    return (
      <MeetingDetailView
        projectId={projectId}
        meetingId={selectedMeetingId}
        onBack={() => setSelectedMeetingId(null)}
      />
    );
  }

  if (list == null) {
    return <div data-testid="meetings-loading" style={{ padding: 24, color: 'var(--text-secondary)' }}>Loading meetings…</div>;
  }

  if (list.length === 0) {
    return (
      <>
        <div
          data-testid="meetings-empty"
          style={{
            padding: 24,
            color: 'var(--text-secondary)',
            fontFamily: 'var(--font-body)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 12,
          }}
        >
          <div>No meetings ingested yet. Upload a Teams VTT or plain-text transcript to get started.</div>
          <UploadButton onClick={() => setShowUpload(true)} />
        </div>
        {uploadDialog}
      </>
    );
  }

  return (
    <>
      <div data-testid="meetings-list" style={{ padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--text)', margin: 0 }}>
            Meetings
          </h2>
          <UploadButton onClick={() => setShowUpload(true)} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {list.map(m => (
            <MeetingRow key={m.id} meeting={m} onOpen={() => setSelectedMeetingId(m.id)} />
          ))}
        </div>
      </div>
      {uploadDialog}
    </>
  );
}

function UploadButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      data-testid="meetings-upload-button"
      onClick={onClick}
      style={{
        padding: '8px 16px',
        fontFamily: 'var(--font-display)',
        fontSize: 13,
        fontWeight: 600,
        color: 'white',
        background: 'var(--primary)',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        cursor: 'pointer',
      }}
    >
      Upload Transcript
    </button>
  );
}

function MeetingRow({ meeting, onOpen }: { meeting: MeetingListItem; onOpen: () => void }) {
  const counts = meeting.transcript?.counts;
  const totalPending = counts
    ? counts.decisions.pending + counts.actions.pending + counts.risks.pending
    : 0;
  const status = meeting.transcript?.extractionStatus ?? 'PENDING';
  return (
    <button
      data-testid={`meeting-row-${meeting.id}`}
      onClick={onOpen}
      style={{
        textAlign: 'left',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: '14px 16px',
        cursor: 'pointer',
        fontFamily: 'var(--font-body)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{meeting.title}</span>
        <ExtractionStatusBadge status={status} />
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
        {meeting.meetingDate ? new Date(meeting.meetingDate).toLocaleDateString() : 'No date'} ·
        &nbsp;{meeting.attendees.length} attendee{meeting.attendees.length === 1 ? '' : 's'}
      </div>
      {counts && (
        <div data-testid={`meeting-counts-${meeting.id}`} style={{ marginTop: 8, display: 'flex', gap: 14, fontSize: 12 }}>
          <Pill label="Decisions" total={counts.decisions.total} pending={counts.decisions.pending} />
          <Pill label="Actions"   total={counts.actions.total}   pending={counts.actions.pending} />
          <Pill label="Risks"     total={counts.risks.total}     pending={counts.risks.pending} />
          {totalPending > 0 && (
            <span style={{ marginLeft: 'auto', color: 'var(--primary)', fontWeight: 600 }}>
              {totalPending} pending review
            </span>
          )}
        </div>
      )}
    </button>
  );
}

function Pill({ label, total, pending }: { label: string; total: number; pending: number }) {
  return (
    <span style={{ color: 'var(--text-secondary)' }}>
      {label}: <strong style={{ color: 'var(--text)' }}>{total}</strong>
      {pending > 0 && (
        <span style={{ color: 'var(--primary)' }}> ({pending} pending)</span>
      )}
    </span>
  );
}

function ExtractionStatusBadge({ status }: { status: ExtractionStatus }) {
  const colour =
    status === 'COMPLETED' ? 'var(--success)' :
    status === 'IN_PROGRESS' ? 'var(--primary)' :
    status === 'FAILED' ? 'var(--error)' :
    'var(--text-secondary)';
  return (
    <span
      data-testid="extraction-status"
      style={{
        fontSize: 11,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: colour,
        fontWeight: 600,
      }}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

// ─── Detail view ──────────────────────────────────────────────────────

function MeetingDetailView({
  projectId, meetingId, onBack,
}: {
  projectId: string;
  meetingId: string;
  onBack: () => void;
}) {
  const [detail, setDetail] = useState<MeetingDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<MeetingDetail>(`/projects/${projectId}/meetings/${meetingId}`)
      .then(r => { if (!cancelled) setDetail(r); })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load meeting'); });
    return () => { cancelled = true; };
  }, [projectId, meetingId]);

  if (error) {
    return <div data-testid="meeting-detail-error" style={{ padding: 24, color: 'var(--error)' }}>{error}</div>;
  }
  if (!detail) {
    return <div data-testid="meeting-detail-loading" style={{ padding: 24, color: 'var(--text-secondary)' }}>Loading…</div>;
  }

  const transcript = detail.transcript;

  // Optimistic update: replace one item in one category.
  const replaceItem = (category: ItemCategory, index: number, next: MeetingItemBase) => {
    setDetail(d => {
      if (!d || !d.transcript) return d;
      const arr = (
        category === 'decisions' ? d.transcript.extractedDecisions :
        category === 'actions'   ? d.transcript.extractedActions   :
                                   d.transcript.extractedRisks
      ).slice() as MeetingItemBase[];
      arr[index] = { ...arr[index], ...next };
      return {
        ...d,
        transcript: {
          ...d.transcript,
          ...(category === 'decisions' ? { extractedDecisions: arr as DecisionItem[] } :
              category === 'actions'   ? { extractedActions:   arr as ActionItem[] }   :
                                          { extractedRisks:    arr as RiskItem[] }),
        },
      };
    });
  };

  return (
    <div data-testid="meeting-detail" style={{ padding: 24 }}>
      <button
        onClick={onBack}
        data-testid="back-to-list"
        style={{ background: 'transparent', border: 'none', color: 'var(--primary)', fontFamily: 'var(--font-body)', cursor: 'pointer', marginBottom: 12 }}
      >
        ← All meetings
      </button>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--text)' }}>{detail.meeting.title}</h2>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 20 }}>
        {detail.meeting.meetingDate ? new Date(detail.meeting.meetingDate).toLocaleDateString() : 'No date'} ·
        &nbsp;Attendees: {detail.meeting.attendees.join(', ') || '(none)'}
      </div>

      {!transcript && (
        <div data-testid="no-transcript">No transcript ingested.</div>
      )}

      {transcript?.extractionStatus !== 'COMPLETED' && transcript && (
        <div
          data-testid="extraction-incomplete"
          style={{
            padding: '12px 16px',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--surface-alt)',
            color: 'var(--text-secondary)',
            fontFamily: 'var(--font-body)',
            fontSize: 13,
          }}
        >
          Extraction status: {transcript.extractionStatus}.
          {transcript.extractionError && (
            <div style={{ marginTop: 6, color: 'var(--error)' }}>{transcript.extractionError}</div>
          )}
        </div>
      )}

      {transcript?.extractionStatus === 'COMPLETED' && (
        <>
          <CategorySection
            title="Decisions"
            category="decisions"
            items={transcript.extractedDecisions}
            renderTitle={d => d.title}
            renderSubtitle={d => d.rationale}
            projectId={projectId}
            meetingId={meetingId}
            transcriptId={transcript.id}
            onItemUpdate={(idx, next) => replaceItem('decisions', idx, next)}
          />
          <CategorySection
            title="Actions"
            category="actions"
            items={transcript.extractedActions}
            renderTitle={a => a.description}
            renderSubtitle={a => a.assignee ? `Assignee: ${a.assignee}` : null}
            projectId={projectId}
            meetingId={meetingId}
            transcriptId={transcript.id}
            onItemUpdate={(idx, next) => replaceItem('actions', idx, next)}
          />
          <CategorySection
            title="Risks"
            category="risks"
            items={transcript.extractedRisks}
            renderTitle={r => r.description}
            renderSubtitle={r => r.impact ? `Impact: ${r.impact}` : null}
            projectId={projectId}
            meetingId={meetingId}
            transcriptId={transcript.id}
            onItemUpdate={(idx, next) => replaceItem('risks', idx, next)}
          />
        </>
      )}
    </div>
  );
}

interface CategorySectionProps<T extends MeetingItemBase> {
  title:        string;
  category:     ItemCategory;
  items:        T[];
  renderTitle:    (item: T) => string;
  renderSubtitle: (item: T) => string | null | undefined;
  projectId:    string;
  meetingId:    string;
  transcriptId: string;
  onItemUpdate: (index: number, next: MeetingItemBase) => void;
}

function CategorySection<T extends MeetingItemBase>({
  title, category, items, renderTitle, renderSubtitle,
  projectId, meetingId, transcriptId, onItemUpdate,
}: CategorySectionProps<T>) {
  return (
    <section data-testid={`section-${category}`} style={{ marginTop: 24 }}>
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, color: 'var(--text)', marginBottom: 8 }}>
        {title} <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>({items.length})</span>
      </h3>
      {items.length === 0 && (
        <div style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)', fontSize: 13 }}>
          None extracted.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((item, idx) => (
          <ItemCard
            key={`${category}-${idx}`}
            item={item}
            title={renderTitle(item)}
            subtitle={renderSubtitle(item) ?? null}
            projectId={projectId}
            meetingId={meetingId}
            transcriptId={transcriptId}
            category={category}
            itemIndex={idx}
            onUpdate={next => onItemUpdate(idx, next)}
          />
        ))}
      </div>
    </section>
  );
}

interface ItemCardProps {
  item:          MeetingItemBase;
  title:         string;
  subtitle:      string | null;
  projectId:     string;
  meetingId:     string;
  transcriptId:  string;
  category:      ItemCategory;
  itemIndex:     number;
  onUpdate:      (next: MeetingItemBase) => void;
}

function ItemCard({ item, title, subtitle, projectId, meetingId, transcriptId, category, itemIndex, onUpdate }: ItemCardProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const status = item.reviewStatus ?? 'pending';

  const callAction = async (verb: 'approve' | 'edit' | 'dismiss', body?: unknown) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch<{ item: MeetingItemBase; canonicalId?: string; alreadyApproved?: boolean }>(
        `/projects/${projectId}/meetings/${meetingId}/transcripts/${transcriptId}/items/${category}/${itemIndex}/${verb}`,
        { method: 'POST', body: body ? JSON.stringify(body) : '{}' },
      );
      onUpdate(res.item);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${verb}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`item-card-${category}-${itemIndex}`}
      data-review-status={status}
      style={{
        padding: '12px 14px',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        background: status === 'dismissed' ? 'var(--surface-alt)' : 'var(--surface)',
        opacity: status === 'dismissed' ? 0.6 : 1,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text)', fontWeight: 500 }}>{title}</div>
          {subtitle && (
            <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
              {subtitle}
            </div>
          )}
        </div>
        <ItemStatusBadge status={status} />
      </div>

      {status === 'pending' && (
        <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
          <ActionButton testid={`approve-${category}-${itemIndex}`} variant="primary" disabled={busy}
            onClick={() => callAction('approve')}>Approve</ActionButton>
          <ActionButton testid={`dismiss-${category}-${itemIndex}`} variant="muted" disabled={busy}
            onClick={() => callAction('dismiss')}>Dismiss</ActionButton>
          {/* Edit is intentionally a placeholder for the v1 ship — the
             modal/inline editor is a follow-up. The backend endpoint is
             live; the click below opens a single-field prompt() so a
             reviewer can fix a typo on the rendered title without
             losing extraction structure. */}
          <ActionButton testid={`edit-${category}-${itemIndex}`} variant="muted" disabled={busy}
            onClick={() => {
              const next = window.prompt('Edit ' + (category === 'decisions' ? 'title' : 'description'), title);
              if (next == null || next.trim() === title) return;
              const patch = category === 'decisions' ? { title: next.trim() } : { description: next.trim() };
              void callAction('edit', { patch });
            }}>Edit</ActionButton>
        </div>
      )}

      {status === 'approved' && item.canonicalId && (
        <div data-testid={`canonical-id-${category}-${itemIndex}`} style={{ marginTop: 8, fontSize: 11, color: 'var(--text-secondary)' }}>
          Promoted to canonical row <code>{item.canonicalId}</code>.
        </div>
      )}

      {error && (
        <div data-testid={`item-error-${category}-${itemIndex}`} style={{ marginTop: 8, fontSize: 12, color: 'var(--error)' }}>
          {error}
        </div>
      )}
    </div>
  );
}

function ItemStatusBadge({ status }: { status: ItemReviewStatus }) {
  const colour =
    status === 'approved'  ? 'var(--success)' :
    status === 'dismissed' ? 'var(--text-secondary)' :
                             'var(--primary)';
  return (
    <span
      data-testid="item-status-badge"
      style={{
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: colour,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {status}
    </span>
  );
}

function ActionButton({
  children, onClick, variant, disabled, testid,
}: {
  children: React.ReactNode;
  onClick: () => void;
  variant: 'primary' | 'muted';
  disabled: boolean;
  testid: string;
}) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '6px 12px',
        borderRadius: 'var(--radius-sm)',
        border: variant === 'primary' ? 'none' : '1px solid var(--border)',
        background: variant === 'primary' ? 'var(--primary)' : 'transparent',
        color: variant === 'primary' ? '#fff' : 'var(--text-secondary)',
        fontFamily: 'var(--font-body)',
        fontSize: 12,
        fontWeight: 600,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}
